import { useContext } from 'react'
import { Button, Card } from 'react-bootstrap'
import parse from 'html-react-parser'
import cn from 'classnames'

import CourseContext from 'components/common/contexts/CourseContext'
import { navigateTo } from 'components/common/navigation'
import { ReactComponent as IconJourneys } from '../../../assets/images/icons/icon-journeys.svg'
import { useHistory } from 'react-router-dom'
import { getCoursePath } from 'components/common/helpers/Paths'
import { buildRequestURL } from 'components/common/api'

/**
 * User Program Course component: used to display a course within the user's program
 */
export default function UserProgramCourse({
  courseId,
  courseIndex = 0,
  previewOnly = false,
  showProgress = true,
  className = '',
  returnPath = '',
  programCourses = []
}) {
  const history = useHistory()
  const { courses, progress } = useContext(CourseContext)

  // Return early if we don't have the courses data
  if (!courses?.length) return null

  // Find the course and progress data
  const course = courses.find(course => course.id === parseInt(courseId))

  // Return early if we don't have a course
  if (!course) return null

  // Find the course progress
  const courseProgress = progress?.length
    ? progress.find(match => match.course === course.id)
    : null

  // Default to 1 to avoid divide by 0 and all courses have at least 1 step
  const stepsTotal = courseProgress?.steps_total || 1
  const stepsCompleted = courseProgress?.steps_completed || 0

  // If this isn't the first item, get the previous course
  const previousCourse =
    courseIndex > 0 ? programCourses[courseIndex - 1] : null

  // Look for the previous course progress
  const previousCourseProgress = progress?.length
    ? progress.find(match => match.course === previousCourse?.id)
    : null

  const previousCourseCompleted = previousCourseProgress?.date_completed

  // Lock the course content if...
  const isLocked =
    // ...we're not in preview mode
    !previewOnly &&
    // ...this isn't the first course
    courseIndex > 0 &&
    // ...the previous course hasn't been completed
    !previousCourseCompleted &&
    // ...we haven't completed any steps
    stepsCompleted === 0

  /**
   * Event handler for when the user clicks the "Begin session" button on the Course
   */
  const handleCourseClick = courseId => {
    const path = getCoursePath(courseId)
    const url = buildRequestURL(path, {
      returnPath: returnPath || history.location.pathname,
      startNextSession: true
    })
    navigateTo(url, history)
  }

  return (
    <Card
      key={courseIndex}
      className={cn('user-program-course', {
        [className]: className,
        locked: isLocked
      })}
    >
      <Card.Body>
        <Card.Title>
          <span className='icon'>
            <IconJourneys />
          </span>
          {parse(course.clean_title)}

          {isLocked ? (
            <i className='bi bi-lock'></i>
          ) : (
            <>
              {/* Should we show progress? */}
              {showProgress && (
                <small className='d-block mt-2 user-program-course-progress'>
                  {/* If we've completed all the sessions, show a checkmark */}
                  {stepsCompleted === stepsTotal && (
                    <i className='bi bi-check-circle green'></i>
                  )}{' '}
                  Completed {stepsCompleted} of {stepsTotal} sessions
                </small>
              )}
            </>
          )}
        </Card.Title>

        {/* Don't show card text if the course is locked */}
        {!isLocked && (
          <Card.Text as='div'>
            {parse(course.description)}
            {!previewOnly && (
              <div className='user-program-course-actions'>
                {/* Don't show the "Next session" button if we've completed all the sessions */}
                {stepsCompleted < stepsTotal && (
                  <Button
                    variant='light'
                    size='large'
                    className='w-100 text-start'
                    onClick={_ => handleCourseClick(courseId)}
                  >
                    <i className='bi bi-fast-forward'></i>
                    {stepsCompleted > 0 ? 'Next session' : 'Start session'}
                  </Button>
                )}
              </div>
            )}
          </Card.Text>
        )}
      </Card.Body>
    </Card>
  )
}
